import React from 'react';
import { Article, ArticleStatus } from '../types';
import { 
  DocumentTextIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';

/**
 * Zentrale Status-Funktionen für Artikel
 * Basiert auf der DashboardPage-Implementierung (vollständigste Version)
 */

export const getStatusColor = (status: ArticleStatus): string => {
  switch (status) {
    case 'draft':
      return 'bg-gray-100 text-gray-800';
    case 'lektoriert':
      return 'bg-blue-100 text-blue-800';
    case 'metadata_ok':
      return 'bg-yellow-100 text-yellow-800';
    case 'image_ok':
      return 'bg-purple-100 text-purple-800';
    case 'ready':
      return 'bg-green-100 text-green-800';
    case 'scheduled':
      return 'bg-indigo-100 text-indigo-800';
    case 'publishing':
      return 'bg-orange-100 text-orange-800';
    case 'published':
      return 'bg-green-100 text-green-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'rework_requested':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getStatusIcon = (status: ArticleStatus): React.ReactNode => {
  switch (status) {
    case 'draft':
      return <DocumentTextIcon className="w-4 h-4" />;
    case 'scheduled':
      return <ClockIcon className="w-4 h-4" />;
    case 'published':
      return <CheckCircleIcon className="w-4 h-4" />;
    case 'failed':
    case 'rework_requested':
      return <ExclamationTriangleIcon className="w-4 h-4" />;
    default:
      return <DocumentTextIcon className="w-4 h-4" />;
  }
};

export const getStatusText = (status: ArticleStatus): string => {
  switch (status) {
    case 'draft':
      return 'Entwurf';
    case 'lektoriert':
      return 'Lektoriert';
    case 'metadata_ok':
      return 'Metadaten OK';
    case 'image_ok':
      return 'Bild OK';
    case 'ready':
      return 'Eingereicht';
    case 'scheduled':
      return 'Geplant';
    case 'publishing':
      return 'Wird veröffentlicht';
    case 'published':
      return 'Veröffentlicht';
    case 'failed':
      return 'Fehlgeschlagen';
    case 'rework_requested':
      return 'Überarbeitung erforderlich';
    default:
      return status;
  }
};

// Ein Datum bestätigt keine Veröffentlichung. Die bestätigte Server-Angabe hat Vorrang.
export const getArticleDisplayStatus = (article: Pick<Article, 'status' | 'publicationState'>): ArticleStatus => {
  if (article.status === 'published' || article.status === 'rework_requested' || article.status === 'failed') return article.status;
  if (article.publicationState === 'failed') return 'failed';
  if (article.publicationState === 'queued') return 'ready';
  if (article.publicationState === 'sending' || article.publicationState === 'uncertain') return 'publishing';
  if (article.publicationState === 'accepted') return 'scheduled';
  return article.status;
};

export const getArticleStatusText = (article: Pick<Article, 'status' | 'publicationState'>): string => {
  if (article.status !== 'published' && article.status !== 'rework_requested' && article.status !== 'failed') {
    if (article.publicationState === 'queued') return 'Veröffentlichung beauftragt';
    if (article.publicationState === 'uncertain') return 'Übergabe unbestätigt';
    if (article.publicationState === 'sending') return 'Wird übergeben';
    if (article.publicationState === 'accepted') return 'Geplant · Übergabe bestätigt';
  }
  return getStatusText(article.status);
};

export const articleStatusGroups = [
  { id: 'attention', label: 'Handlungsbedarf', statuses: ['failed', 'rework_requested'] },
  { id: 'drafts', label: 'Entwürfe und Bearbeitung', statuses: ['draft', 'lektoriert', 'metadata_ok', 'image_ok'] },
  { id: 'submitted', label: 'Eingereicht', statuses: ['ready'] },
  { id: 'scheduled', label: 'Geplant', statuses: ['scheduled'] },
  { id: 'publishing', label: 'Übertragung und Veröffentlichung', statuses: ['publishing'] },
  { id: 'published', label: 'Veröffentlicht', statuses: ['published'] }
];

export function groupArticlesByStatus(articles: Article[]) {
  const groups = articleStatusGroups.map(group => ({ ...group, articles: articles.filter(a => group.statuses.includes(getArticleDisplayStatus(a))) }));
  const known = new Set(groups.flatMap(g => g.articles.map(a => a.id)));
  return [...groups, { id: 'other', label: 'Weitere Artikel', statuses: [], articles: articles.filter(a => !known.has(a.id)) }];
}
